import { useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

function Lightbox({ image, images, currentIndex, onClose, onNext, onPrev }) {
  const touchStartX = useRef(0);
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const prevFocus = useRef(null);

  useEffect(() => {
    prevFocus.current = document.activeElement;
    if (closeBtnRef.current) {
      closeBtnRef.current.focus();
    }

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      if (prevFocus.current && prevFocus.current.focus) {
        prevFocus.current.focus();
      }
    };
  }, [onClose, onNext, onPrev]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) onPrev();
      else onNext();
    }
  };

  const total = images ? images.length : 1;
  const displayIndex = (currentIndex !== undefined ? currentIndex : 0) + 1;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label={`Bildvergrößerung – Bild ${displayIndex} von ${total}`}
    >
      <button
        ref={closeBtnRef}
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 z-10"
        onClick={onClose}
        aria-label="Schließen"
      >
        <X className="w-6 h-6" />
      </button>

      <span className="absolute top-6 left-1/2 -translate-x-1/2 text-white/70 text-sm font-bold">
        {displayIndex} von {total}
      </span>

      <div className="max-w-5xl max-h-[80vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={image.src}
          srcSet={image.srcSet}
          sizes="(max-width: 640px) calc(100vw - 2rem), min(1024px, 100vw - 4rem)"
          alt={image.alt}
          loading="eager"
          decoding="async"
          className="max-w-full max-h-[70vh] sm:max-h-[80vh] object-contain rounded-lg"
        />
        <p className="text-white text-center mt-3 font-bold">{image.caption}</p>
      </div>

      <div className="flex items-center gap-6 mt-4 sm:hidden">
        <button
          className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Vorheriges Bild"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Nächstes Bild"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <button
        className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white items-center justify-center hover:bg-white/20"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        aria-label="Vorheriges Bild"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white items-center justify-center hover:bg-white/20"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        aria-label="Nächstes Bild"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
}

export default Lightbox;
